// janus-engine/inventory/destroy-object.ts — R1: the first client-truth INVENTORY-DESTRUCTION slice.
//
// PURE. Resolves "this object is destroyed" (R1: the GM's Turn to Rubble; R2: an object floored by a
// client-resolved hit) against the roster row + catalog the browser already holds, and produces the commit
// envelope (`janus.inventory.destroy.v1`) plus the frame every browser applies to its encounter roster.
// Native composition → fate (vanish / rubble, Talon → Bone) → sizeless anchor + inventory_size
//   → spawn at the source's x/y on the floor under it, FITTED to the source model (ft_rubble_fit_transform)
//   → soft-delete the source → log row.
// Three outcomes, exactly as A1:
//   supported   — command + frame; the caller mutates the roster, renders, and commits (publish-first).
//   refused     — the legacy tool would refuse with this exact message; nothing is sent anywhere.
//   unsupported — the caller hydrates missing inputs or names the unsupported operation; no PHP evaluation.

import { isRubbleAnchorId, rubbleSpawnEntry, RUBBLE_DEFAULT_SIZE_ID } from './rubble-spawn-table.js';
import { normalizeRubbleSource, type RubbleSource } from '../../../../ft3d/engine/rubble-source.js';
// R2-3 (import only — R2 owns the file): the splash a spilled child takes and the object-damage step it goes through.
import { applyObjectDamageTotal, splashDamageTotal } from './object-fall-damage.js';
// R1b-2: the payload snapshot (enqueue half); resolving it is Lane A's pipeline (destruction-payload.ts header).
import { snapshotDestructionPayload, type DestructionPayloadJob, type PayloadCaster } from './destruction-payload.js';

type Row = Record<string, unknown>;

export const OBJECT_DESTROY_SCHEMA = 'janus.inventory.destroy.v1' as const;
export const INVENTORY_TRANSFORM_3D_SCHEMA = 'ft.inventory-transform-3d.v1' as const;
const METERS_PER_FOOT = 0.3048;

export interface ObjectDestroyReaders {
  /** ftLayerValue twin over row + catalog (row override first, then the template's layer). */
  value: (row: Row, name: string) => unknown;
  /** ftLayerEntries twin. */
  entries: (row: Row, name: string) => unknown[];
  /** Full catalog definition {items, item_layer_types, …} for an item id, or null when the client lacks it. */
  definition: (itemId: string) => Row | null;
  /** The floor authored under (x, y) in the encounter shape (ft_shape_floor_at twin); 0 outside every box. */
  floorAt: (x: number, y: number) => number;
  /** Measured bounds [x, y(up), z] in metres of an item's world mesh (catalog `boundsM`), or null. */
  meshBoundsM: (itemId: string) => [number, number, number] | null;
  /** SizeType `base_size` in feet for a size id (window.SIZE_TYPES), or null. */
  sizeBaseFt: (sizeId: string) => number | null;
  /** Fresh uppercase UUID (operation id / rubble inventory id). */
  uuid: () => string;
  /** R1b-3b: a spilled child's splash facts (R2's objectFallUnmodelled gate, hydrated vitality, fragile composition), or null
   * when this page cannot establish them — the whole spill then hands back. Absent = splash never converts. */
  splash?: (child: Row) => SplashFacts | null;
  /** Round 5 session C: a row's DRAWN source geometry (its world mesh × transform × size ratio — the glue's drawnSourceGeometry), for a
   * spilled child the splash destroys (it was never placed, so the caller cannot pass its footprint). Absent = the size-category fallback. */
  drawnGeometry?: (row: Row) => RubbleSource | null;
  /** D-048: whether a row carries live encounter links the page cannot see (another encounter's row pointing at it — legacy
   * inventory_has_live_encounter_links reads both ends globally). True / false, or null when not known yet (the caller fetches it:
   * `linkFactIds`). Absent = only the links this roster shows count. */
  hiddenLinks?: (inventoryId: string) => boolean | null;
  /** The dead creature's own live possessions; null when this page has not loaded them. */
  corpseBody?: (encounterCreatureId: string) => Row[] | null;
  /** N10 (R-021 step 2): does a painted-wall anchor (a persistent spell AoE on this encounter whose `sections` list this row) bind this
   * wall row? True / false, or null when the page cannot read the encounter's anchors. Absent = unknown (an unbound wall hands back). */
  paintedWallBound?: (inventoryId: string) => boolean | null;
}

export interface ObjectDestroyInput {
  campaignId: string;
  encounterId: string;
  inventoryId: string;
  isGM: boolean;
  /** The source placement's RENDERED footprint in feet (larger horizontal axis), read off the scene at the moment
   * of destruction. The browser is the only place that knows the model's true drawn size; without it the fit falls
   * back to the source's size-category footprint, exactly as the PHP lane does. */
  sourceFootprintFt?: number | null;
  sourceGeometry?: RubbleSource | null;
  /** R1b: the branches THIS caller's commit can persist. The GM Turn-to-Rubble lane passes them; the I1 fall lane's own
   * commit cannot migrate links or spill contents yet, so planDamageDestruction() passes none and those still hand back. */
  branches?: { door?: boolean; spill?: boolean; wall?: boolean; payload?: boolean; corpse?: boolean };
  /** R1b-2: who a destruction payload is attributed to (legacy `attributed_caster`) — required with `branches.payload`. */
  payloadCaster?: PayloadCaster | null;
  /** R1b-2: ft_damage_types_in_roll() of the breaking roll (gates `requires_destruction_by_damage_type` payloads). The GM tool's
   * stub roll carries none; a damage lane passes its roll's types. */
  destroyedByDamageTypes?: string[];
  /** R1b-2b: a destruction CAUSED by a committed payload step's damage (legacy handle_inventory_destroyed_from_damage() inside the payload's
   * janus()): an already-rubble pile or a composition that leaves nothing VANISHES — both of which the GM tool refuses — and the log rows
   * go under the parent destruction's roll group. */
  cause?: PayloadCause | null;
  /** R1b-2e: the damage that broke it (legacy `$breaking_damage` = the breaking roll's total) — a container's splash scales with it. The GM
   * tool's stub roll deals 1 (TURN_TO_RUBBLE_BREAKING_DAMAGE, the default). */
  breakingDamage?: number;
  /** Session C follow-up 2 (autopilot W5): this row is a child the GM tool's own spill SPLASH destroys — destroyed by damage (legacy
   * apply_damage_to_target → handle_inventory_destroyed_from_damage), so it vanishes / re-rubbles like a caused destruction, but under the
   * GM destruction's roll group (stamped by the root) instead of a payload's. */
  splashChild?: true;
}

/** R1b-2b: the payload step that destroyed this object — its rows go under `roll_id`, logged with the payload ability as the item used. */
export interface PayloadCause { kind: 'payload'; roll_id: string; source_inventory_id: string; ability_id: string; items?: Row }

/** R1b-3: what a broken container's contents do (spill_container_contents_and_apply_splash). */
export interface ContainerSpill {
  /** Every row that loses cohesion (a vanishing composition) and is soft-deleted, nested ones first — legacy order. */
  delete: string[];
  /** The `inventory_container_spilled` log row: written only when the container held anything. */
  log: boolean;
  /** R1b-3b: every child that keeps its shape — reparented onto the floor and splash-damaged, in legacy walk order. */
  release?: SpillRelease[];
  /** Round 5 session C: every child the splash DESTROYS — legacy release_or_destroy_inventory_after_container_break() reparents it onto
   * the floor at its spill slot, then apply_damage_to_target() destroys it (handle_inventory_destroyed_from_damage() under the parent's
   * roll group). Each is its own full destruction command (`before` = the landing spot on the floor), in legacy walk order; the save
   * reparents and destroys each one before the spill line. Only a CAUSED container carries these (a GM Turn to Rubble hands back). */
  destroyed?: ObjectDestroyCommand[];
}
/** A child the splash destroys, resolved by the caller: its destruction, a hand-back, or the definitions to fetch first. */
export type SpillChildDestruction = ObjectDestroyCommand | { reason: string } | { definitionIds: string[] };

export interface SplashFacts { unmodelled: string | null; vCurrent: number; vTemp: number; effectiveVMax: number; fragile: boolean }
/** One released child (release_or_destroy_inventory_after_container_break's non-NA branch), its vitality CAS and result. */
export interface SpillRelease {
  inventory_id: string;
  /** Its template — part of the server's CAS (a different item under the same id is stale). */
  inventory_item_id: string;
  /** The row it was in (the container, or a cohesion-losing container inside it). */
  possess_id: string;
  item_qty: number;
  x_pos: number; y_pos: number; z_pos: number;
  before: { v_current: number; v_temp: number };
  v_current: number; v_temp: number;
}
/** The breaking damage of the GM tool's destruction (turn_inventory_to_rubble_inc.php's stub roll: total 1). */
export const TURN_TO_RUBBLE_BREAKING_DAMAGE = 1;
/** The spill offset per released child (legacy: slot × 0.35 ft on x AND z). */
export const SPILL_SLOT_FT = 0.35;

/** One encounter_links row as build_encounter_link_arr() serves it (pure-row, bare scalars). */
export type EncounterLinkRow = Record<string, unknown> & { encounter_link_id: string; encounter_link_from: string; encounter_link_to: string };

export interface RubbleTransform3d {
  schema: typeof INVENTORY_TRANSFORM_3D_SCHEMA;
  scaleMode: 'absolute';
  scale?: number;
  rubbleSource?: RubbleSource;
}

export interface RubbleRosterRow {
  inventory: {
    inventory_id: string;
    inventory_item_id: string;
    inventory_name: string;
    possess_id: string;
    item_qty: 1;
    inventory_size?: string;
    transform_3d?: RubbleTransform3d;
    /** R1b-4: a wall SECTION's rubble carries the gap it leaves as `wallBreach` (the scene's breach overlay opens it). */
    inventory_generation?: Row;
  };
  position: { x_pos: number; y_pos: number; z_pos: number };
  permissions: { player: true; campaign: true };
  computed: { possessor: { type: 'encounter' } };
  /** R1b-1: a destroyed door's links, re-anchored on its rubble (build_inventory_arr serves them top-level). */
  inventory_encounter_link?: EncounterLinkRow[];
}

/** R1b-1: a roster row whose own link points AT the destroyed door is re-aimed at the rubble. */
export interface LinkRetarget { inventory_id: string; encounter_link_id: string; was: string; to: string }

export interface ObjectDestroyFrame {
  remove: string[];
  add: RubbleRosterRow[];
  retarget?: LinkRetarget[];
  /** R1b-4: a whole wall RUN is breached in place — its row stays, at 0 vitality (the overlay opens the gap). */
  vitality?: Array<{ inventory_id: string; v_current: number }>;
  /** R1b-3b: the spilled children that keep their shape, as floor rows (reparented, splash-damaged). */
  released?: Row[];
  creaturesRemoved?: string[];
}

/** DAFT3D S14 wall-run binding (ft_normalize_inventory_generation_wall_run, the fields a breach reads). */
export interface WallRunBinding { key: string; axis: 'x' | 'y'; at: number; span: { min: number; max: number }; z: { min: number; max: number }; floorFt?: number; sceneWallId?: string }
/** R1b-4: FT_WALL_RUN_SECTION_FT — a run this short is a carved SECTION: it leaves rubble; a longer run is breached in place. */
export const WALL_RUN_SECTION_FT = 5;

export interface ObjectDestroyCommand {
  schema: typeof OBJECT_DESTROY_SCHEMA;
  operation_id: string;
  campaign_id: string;
  encounter_id: string;
  inventory_id: string;
  before: {
    inventory_item_id: string;
    possess_id: string;
    item_qty: number;
    inventory_deleted: 0;
    x_pos: number;
    y_pos: number;
    z_pos: number;
  };
  outcome: 'rubble' | 'vanish' | 'breach';
  rubble: {
    inventory_id: string;
    inventory_item_id: string;
    inventory_size: string | null;
    inventory_name: string;
    x_pos: number;
    y_pos: number;
    z_pos: number;
    transform_3d: RubbleTransform3d | null;
    saved_row?: Row;
  } | null;
  log: { key: 'inventory_destroyed_rubble' | 'inventory_destroyed_vanished'; original_name: string };
  frame: ObjectDestroyFrame;
  /** R1b-1 (doors only): the live links this page saw anchored on the door — `from` on the door row, `to` on rows of this
   * roster. The server compares both sets under lock (a changed set is stale) and migrates every link to the rubble
   * (migrate_encounter_links_inventory_anchor), including `to` links anchored in other encounters, which no page sees. */
  links?: { from: string[]; to: Array<{ encounter_link_id: string; inventory_id: string }> };
  /** R1b-3 (containers only): the contents this page saw and their fate; the server re-derives and compares under lock. */
  spill?: ContainerSpill;
  /** Root GM-tool corpse destruction: the browser decides integrated deletes and the body's floor spill. */
  corpse?: { encounter_creature_id: string; delete: string[];
    release: Array<{ inventory_id: string; x_pos: number; y_pos: number; z_pos: number }>; name: string;
    /** Ability lane's explicit bulk prepared-spell/picture retirement; status consequences live in its verdict. */
    retireAttachments?: boolean };
  /** R1b-4 (wall runs only): the run the row is bound to and how it goes — a 5 ft `section` leaves rubble carrying the gap,
   * a longer `run` is breached in place (outcome 'breach', no rubble). The server re-derives both from the locked row. */
  wall?: { key: string; kind: 'section' | 'run' };
  /** R1b-2c: the creature the GM tool acts AS (the request's caster_id) — every destruction row is attributed to it, as legacy's `$caster_arr`.
   * Absent = the acting GM user. The caller sets it; the server proves it a live member of this encounter. */
  caster_id?: string;
  /** R1b-2b: set on a destruction a payload step caused (outcome 'rubble' or 'vanish'; the log rows go under `cause.roll_id`). */
  cause?: PayloadCause;
  /** A child the GM tool's own spill splash destroyed (autopilot W5): its rows go under the ROOT destruction's roll group (legacy passes the
   * GM destruction's `$roll_id` down), logged as the GM tool's. Set by the root once its own operation id exists. */
  roll_group?: string;
  /** R1b-2 (payload objects only, with `branches.payload`): the job legacy enqueues, snapshotted BEFORE the frame mutates the row.
   * The server compares its ability list with the locked row's; the initiator drains it after the commit (Lane A's runner). */
  payload?: DestructionPayloadJob;
  /** D-048 (a CAUSED vanish only): legacy spawn_waypoint_map_marker_for_destroyed_door() — a door that leaves no rubble, or an already-rubble
   * pile carrying live links, leaves a map marker at its spot and every link migrates onto it (both ends). The page mints its id. */
  waypoint?: { inventory_id: string; inventory_item_id: string; inventory_name: string; x_pos: number; y_pos: number; z_pos: number; saved_row?: Row };
  /** N10 (R-021, the GM tool's ROOT only): the row was HELD by a creature of this encounter (directly or inside its pack). It falls to the
   * floor at its holder's feet first (legacy ft_corpse_drop_held_body_for_destruction's rule — worker pick: every held row, not only a body),
   * then is destroyed as a floor row. The page resolves it on a view holding the dropped row; the server proves the possessor chain still
   * ends at `holder_id` with `from_possess_id` as the direct possessor, then writes the drop (possess → encounter, unequipped, the spot). */
  held?: { holder_id: string; from_possess_id: string; x_pos: number; y_pos: number; z_pos: number };
}

/** MapMarker::LIST (includes/additions.php): the direction markers a destroyed door's waypoint is drawn from. */
export const MAP_MARKER_IDS = {
  east: 'C7814403-F84C-F6F9-0794-95F3EC3AE394', north: 'F5DDC0AA-581A-8E6F-EFAF-4445D1A3EBC3', northeast: 'ACE453E8-5725-AB18-0BCF-589DDA35135D',
  northwest: 'BF941177-6C2F-4EF6-92C8-BF45E0E1BE52', south: '85436C19-426D-491A-E049-42731CF8AB70', southeast: 'D93EF021-9229-85C0-46DB-43F0E7E0FE9D',
  southwest: '73F0A293-AE4D-5E0C-9A0A-59F46FE20F5D', west: '44B43903-216C-386A-24BE-5DE3BCBB8E0D',
} as const;
/** MapMarker::resolveTemplateItemIdFromDoorHints(): compound directions before simple ones, in the name hints (instance + template), else North. */
export function waypointMarkerFromHints(...hints: string[]): string {
  const blob = hints.filter(h => h !== '').join(' ').toLowerCase().replace(/[-_]/g, ' ');
  for (const dir of ['northeast', 'northwest', 'southeast', 'southwest', 'north', 'south', 'east', 'west'] as const)
    if (blob.includes(dir)) return MAP_MARKER_IDS[dir];
  return MAP_MARKER_IDS.north;
}

export type ObjectDestroyScope =
  | { supported: true; command: ObjectDestroyCommand }
  | { supported: false; refused: true; message: string }
  /** `anchorItemId` rides `rubble-anchor-not-in-catalog`: the caller can fetch that one definition and retry. */
  | { supported: false; refused: false; reason: string; anchorItemId?: string; definitionIds?: string[]; linkFactIds?: string[]; corpseCreatureIds?: string[] };

export const LEGACY_NOT_ELIGIBLE = 'This inventory cannot be turned to rubble (composition does not leave rubble, rubble template is missing, or it is not under an encounter).';

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const uuidRe = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const isUuid = (value: unknown): boolean => uuidRe.test(upper(value));
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

function bucketField(row: Row | null, bucket: string, key: string): unknown {
  const b = record(row?.[bucket]);
  return scalar(b?.[key] ?? row?.[key]);
}
const inventoryField = (row: Row | null, key: string): unknown => bucketField(row, 'inventory', key);
const itemField = (row: Row | null, key: string): unknown => bucketField(row, 'items', key);
/** The roster holds this creature with positive vitality and no dead / deleted flag (a lean row omits zero flags). A roster stub with
 * no vitality, or no roster row, is not known alive: the body keeps its corpse branch. */
function linkedCreatureAlive(encounter: Row, encounterCreatureId: string): boolean {
  const roster = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : [];
  const member = roster.map(record).find(r => upper(bucketField(r, 'encounter_creatures', 'encounter_creature_id')) === encounterCreatureId);
  if (!member || Number(bucketField(member, 'encounter_creatures', 'encounter_creature_dead') ?? 0) === 1
    || Number(bucketField(member, 'encounter_creatures', 'encounter_creature_deleted') ?? 0) === 1) return false;
  return Number(bucketField(member, 'encounter_creatures', 'v_current') ?? record(record(member.computed)?.vitality)?.v_current ?? 0) > 0;
}
function position(row: Row, key: 'x_pos' | 'y_pos' | 'z_pos'): number {
  const pos = record(row.position) ?? record(row.pos_arr) ?? row;
  const n = Number(scalar(pos[key]) ?? 0);
  return Number.isFinite(n) ? n : 0;
}
/** A layer value as a bare UUID (layers may carry a nested {items:{item_id}} shell or a [0]-wrapped id). */
function layerUuid(read: ObjectDestroyReaders, row: Row, name: string): string | null {
  const raw = scalar(read.value(row, name));
  const id = typeof raw === 'string' ? raw : itemField(record(raw), 'item_id');
  return isUuid(id) ? upper(id) : null;
}

/** is_inventory_arr_a_container twin (functions.php): item_type 'container' or ANY capacity layer present — a row
 * that can hold contents spills them, which touches other rows (legacy lane). */
const CAPACITY_LAYERS = ['carrying_capacity_cu_ft', 'carrying_capacity_cu_in', 'carrying_capacity_people', 'carrying_capacity_pounds', 'carrying_capacity_fixed_weight'];
function isContainer(read: ObjectDestroyReaders, row: Row, itemType: string): boolean {
  if (itemType === 'container') return true;
  return CAPACITY_LAYERS.some(name => {
    const value = scalar(read.value(row, name));
    return (value !== null && value !== undefined && value !== '') || read.entries(row, name).length > 0;
  });
}

/** A row's OWN live links (build_inventory_arr: the rows whose `encounter_link_from` is this row), served top-level —
 * the bucketed read covers a reshaped blob (inventory_is_passage_prop reads both). */
export function ownEncounterLinks(row: Row | null): EncounterLinkRow[] {
  const id = upper(inventoryField(row, 'inventory_id'));
  const raw = row?.inventory_encounter_link ?? record(row?.inventory)?.inventory_encounter_link;
  const list = Array.isArray(raw) ? raw : record(raw) ? Object.values(record(raw)!) : [];
  return list.map(record).filter((link): link is EncounterLinkRow => !!link && isUuid(link.encounter_link_id)
    && Number(scalar(link.encounter_link_deleted) ?? 0) !== 1 && upper(link.encounter_link_from) === id);
}

/** A row's nested contents as served (build_inventory_arr's `inventory_in_container`; the bucketed read covers a reshaped blob). */
function contentsOf(row: Row): Row[] {
  const raw = row.inventory_in_container ?? record(row.inventory)?.inventory_in_container;
  const list = Array.isArray(raw) ? raw : record(raw) ? Object.values(record(raw)!) : [];
  return list.map(record).filter((r): r is Row => !!r && Number(inventoryField(r, 'inventory_deleted') ?? 0) !== 1);
}

/**
 * release_or_destroy_inventory_after_container_break() twin, for the NO-SPLASH case only: a child whose composition
 * vanishes (item_inventory_loses_cohesion_when_container_breaks) is deleted — after its own contents, recursively, when
 * it is itself a container; any child that keeps its shape would be reparented AND splash-damaged → hand back.
 * A child whose template this page lacks cannot be classified: its id is returned for one fetch and a retry.
 */
export function planSpill(read: ObjectDestroyReaders, container: Row, base: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 },
  breakingDamage: number = TURN_TO_RUBBLE_BREAKING_DAMAGE,
  /** Round 5 session C: resolves a child the splash destroys (landing spot, the splash total). Absent = that child hands back. */
  destroyChild?: (child: Row, at: { x: number; y: number; z: number }, splashTotal: number) => SpillChildDestruction):
  ContainerSpill | { reason: string; definitionIds?: string[] } {
  const missing = new Set<string>();
  const del: string[] = [];
  const release: SpillRelease[] = [];
  const destroyed: ObjectDestroyCommand[] = [];
  let handBack: string | null = null;
  let slot = 0;
  const walk = (rows: Row[], depth: number, parentId: string): void => {
    for (const child of rows) {
      const id = upper(inventoryField(child, 'inventory_id'));
      const templateId = upper(inventoryField(child, 'inventory_item_id'));
      if (!isUuid(id) || !isUuid(templateId)) { handBack ??= 'container-spill-unreadable'; continue; }
      const template = read.definition(templateId);
      if (!template) { missing.add(templateId); continue; }
      // The row's own composition layer, else its template's.
      const composition = layerUuid(read, child, 'item_composition');
      const vanishes = rubbleSpawnEntry(composition)?.fate === 'vanishes';
      if (!vanishes) {
        // Keeps its shape: reparented to the floor at the container's spot + slot × 0.35 ft (x AND z, as legacy writes it), then
        // splash-damaged. R2's gate first; a child the splash would DESTROY recurses into its own destruction — legacy's lane.
        const facts = read.splash?.(child) ?? null;
        if (!facts) { handBack ??= 'container-spill-splash'; continue; }
        if (facts.unmodelled) { handBack ??= `spill-child-unmodelled:${facts.unmodelled}`; continue; }
        const splash = splashDamageTotal(breakingDamage, facts.fragile);
        const hit = applyObjectDamageTotal({ total: splash, vCurrent: facts.vCurrent,
          vTemp: facts.vTemp, effectiveVMax: facts.effectiveVMax, destroyedByDamageType: [], damageTypeIds: [] });
        if (hit.destroyed) {
          if (!destroyChild) { handBack ??= 'spill-child-destroyed'; continue; }
          // legacy takes the spill slot BEFORE the splash lands: the child is on the floor at its slot when it is destroyed
          const off = slot++ * SPILL_SLOT_FT;
          const resolved = destroyChild(child, { x: base.x + off, y: base.y, z: base.z + off }, splash);
          if ('definitionIds' in resolved) { resolved.definitionIds.forEach(d => missing.add(d)); continue; }
          if ('reason' in resolved) { handBack ??= resolved.reason; continue; }
          destroyed.push(resolved);
          continue;
        }
        const off = slot++ * SPILL_SLOT_FT;
        release.push({ inventory_id: id, inventory_item_id: templateId, possess_id: parentId, item_qty: Number(inventoryField(child, 'item_qty') ?? 1) || 1,
          x_pos: base.x + off, y_pos: base.y, z_pos: base.z + off,
          before: { v_current: facts.vCurrent, v_temp: facts.vTemp }, v_current: hit.vCurrent, v_temp: hit.vTemp });
        continue;
      }
      const type = String(itemField(template, 'item_type') ?? itemField(child, 'item_type') ?? '');
      if (isContainer(read, child, type)) {
        if (depth >= 24) { handBack ??= 'container-spill-depth'; continue; } // the legacy destruction depth fuse
        walk(contentsOf(child), depth + 1, id);
      }
      del.push(id);
    }
  };
  const top = contentsOf(container);
  walk(top, 1, upper(inventoryField(container, 'inventory_id')));
  if (missing.size > 0) return { reason: 'spill-definition-missing', definitionIds: [...missing].sort() };
  if (handBack) return { reason: handBack };
  return { delete: del, log: top.length > 0, ...(release.length > 0 ? { release } : {}), ...(destroyed.length > 0 ? { destroyed } : {}) };
}

const round4 = (n: number): number => Math.round(n * 10000) / 10000;
/** The row's generation record as served (object or JSON text), or null. */
function generationOf(row: Row): Row | null {
  let raw = inventoryField(row, 'inventory_generation');
  if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return null; } }
  return record(raw);
}
/** ft_normalize_inventory_generation_wall_run twin for a breach: key, axis, finite at / span / z (4 dp), non-empty extents.
 * A binding PHP would not accept is caught by the server's own identity check (the click then goes to the legacy lane). */
export function wallRunBindingOf(value: unknown): WallRunBinding | null {
  const run = record(value);
  const key = typeof run?.key === 'string' ? run.key.trim() : '';
  const axis = String(run?.axis ?? '').trim().toLowerCase();
  if (!run || key === '' || (axis !== 'x' && axis !== 'y')) return null;
  const num = (v: unknown): number | null => (typeof v === 'number' || (typeof v === 'string' && v.trim() !== '')) && Number.isFinite(Number(v)) ? round4(Number(v)) : null;
  const at = num(run.at), smin = num(record(run.span)?.min), smax = num(record(run.span)?.max), zmin = num(record(run.z)?.min), zmax = num(record(run.z)?.max);
  if (at === null || smin === null || smax === null || zmin === null || zmax === null || !(smax > smin) || !(zmax > zmin)) return null;
  const floorFt = num(run.floorFt);
  return { key, axis, at, span: { min: smin, max: smax }, z: { min: zmin, max: zmax }, ...(floorFt !== null ? { floorFt } : {}),
    ...(typeof run.sceneWallId === 'string' && /^[A-Za-z0-9_:.-]{1,64}$/.test(run.sceneWallId) ? { sceneWallId: run.sceneWallId } : {}) };
}

/** Instance override → size layer → template size → rubble default. */
function sourceSizeId(read: ObjectDestroyReaders, row: Row, template: Row | null): string {
  const instance = inventoryField(row, 'inventory_size');
  const instanceId = typeof instance === 'string' ? instance : itemField(record(instance), 'item_id');
  if (isUuid(instanceId)) return upper(instanceId);
  const own = layerUuid(read, row, 'size');
  if (own) return own;
  const fromTemplate = template ? layerUuid(read, template, 'size') : null;
  return fromTemplate ?? RUBBLE_DEFAULT_SIZE_ID;
}

/**
 * ft_rubble_fit_transform twin: an ABSOLUTE scale that makes the rubble mesh cover the source model's footprint.
 * Source footprint = the scene's rendered footprint when the caller supplies it, else the size-category
 * `base_size` (the PHP fallback when the source mesh has no measured bounds). NULL without rubble mesh bounds.
 */
export function fitRubbleTransform(
  read: ObjectDestroyReaders, anchorItemId: string, sourceSize: string, sourceFootprintFt?: number | null,
): RubbleTransform3d | null {
  const bounds = read.meshBoundsM(anchorItemId);
  const rubbleM = bounds ? Math.max(Number(bounds[0]), Number(bounds[2])) : 0;
  if (!(rubbleM > 0)) return null;
  let sourceM = Number(sourceFootprintFt) > 0 ? Number(sourceFootprintFt) * METERS_PER_FOOT : 0;
  if (!(sourceM > 0)) {
    const baseFt = read.sizeBaseFt(sourceSize);
    if (!(baseFt !== null && baseFt > 0)) return null;
    sourceM = baseFt * METERS_PER_FOOT;
  }
  const scale = Math.round(Math.max(0.05, Math.min(24, sourceM / rubbleM)) * 10000) / 10000;
  // ft_normalize_inventory_transform_3d drops a scale of exactly 1 (absolute mode alone then means 1.0).
  return Math.abs(scale - 1) >= 0.0001
    ? { schema: INVENTORY_TRANSFORM_3D_SCHEMA, scaleMode: 'absolute', scale }
    : { schema: INVENTORY_TRANSFORM_3D_SCHEMA, scaleMode: 'absolute' };
}

/**
 * Resolve the destruction of one loose floor object. Never mutates; the caller applies `command.frame`.
 */
export function resolveObjectDestruction(input: ObjectDestroyInput, encounter: Row, read: ObjectDestroyReaders): ObjectDestroyScope {
  const unsupported = (reason: string): ObjectDestroyScope => ({ supported: false, refused: false, reason });
  if (!input.isGM) return unsupported('not-gm');
  const encounterId = upper(input.encounterId);
  const inventoryId = upper(input.inventoryId);
  if (!isUuid(encounterId) || !isUuid(inventoryId) || !isUuid(input.campaignId)) return unsupported('bad-ids');

  const roster = Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as unknown[] : [];
  const row = roster.map(record).find(r => upper(inventoryField(r, 'inventory_id')) === inventoryId) ?? null;
  if (!row) return unsupported('not-a-loose-row');
  if (upper(inventoryField(row, 'possess_id')) !== encounterId) return unsupported('not-a-loose-row');
  if (Number(inventoryField(row, 'inventory_deleted') ?? 0) === 1) {
    return { supported: false, refused: true, message: 'Inventory not found.' };
  }
  // CORPSE-AS-INVENTORY: the GM tool persists the body's own possessions and creature removal — its root body, and (N10, R-021) a body its
  // own spill's splash destroys (legacy: the same handle_inventory_destroyed_from_damage → ft_corpse_body_destroyed under the root's roll
  // group). A body a payload step destroys (`cause`) rides the ability lane's runner: still handed back.
  // A body whose linked creature stands ALIVE on this roster (revived — e.g. by the GM edit form — with its corpse row left behind) is no
  // longer its body: it breaks as the plain object it is, and the living creature keeps its row, gear and statuses (genogash).
  const linkedId = upper(inventoryField(row, 'inventory_encounter_creature_id'));
  const corpseId = isUuid(linkedId) && !linkedCreatureAlive(encounter, linkedId) ? linkedId : '';
  const isCorpse = isUuid(corpseId);
  if (isCorpse && (input.branches?.corpse !== true || input.cause)) return unsupported('corpse-body');
  // Called only after the GM composition refusal: loading a body must not change that refusal.
  const planCorpse = (): { corpse?: ObjectDestroyCommand['corpse']; released: Row[] } | ObjectDestroyScope => {
    if (!isCorpse) return { released: [] };
    const children = read.corpseBody?.(corpseId) ?? null;
    if (children === null) return { supported: false, refused: false, reason: 'corpse-body-unloaded', corpseCreatureIds: [corpseId] };
    const corpse: NonNullable<ObjectDestroyCommand['corpse']> = { encounter_creature_id: corpseId, delete: [], release: [],
      name: String(inventoryField(row, 'inventory_name') ?? 'Corpse') };
    const shape = record(encounter.encounters)?.encounter_shape ?? encounter.encounter_shape;
    const depthAuthored = shape !== null && shape !== undefined && shape !== '';
    const released: Row[] = [];
    let slot = 0;
    for (const child of children) {
      if (upper(inventoryField(child, 'possess_id')) !== corpseId || Number(inventoryField(child, 'inventory_deleted') ?? 0) === 1) continue;
      const id = upper(inventoryField(child, 'inventory_id'));
      const target = record(child.statuses)?.target;
      const statuses = Array.isArray(target) ? target : record(target) ? Object.values(record(target)!) : [];
      // A status row on the page is flat (`status_item_id`) or bucketed (`items.item_id`); the row's type comes from its own bucket,
      // else its template (a caster-cache possession carries no items bucket) — N9 staging smoke: an integrated part was released.
      const INTEGRATED = '36C061BF-E480-4334-961C-26500150E3C4';
      const integrated = statuses.some(status => [record(status)?.status_item_id, itemField(record(status), 'item_id')].some(v => upper(v) === INTEGRATED))
        || String(itemField(child, 'item_type') ?? itemField(read.definition(upper(inventoryField(child, 'inventory_item_id'))) ?? null, 'item_type') ?? '') === 'monster_bit';
      if (integrated) { corpse.delete.push(id); continue; }
      const angle = slot * 2.399963, radius = 0.6 + 0.25 * slot;
      slot++;
      const dx = Math.cos(angle) * radius, dg = Math.sin(angle) * radius;
      const spot = { x_pos: position(row, 'x_pos') + dx, y_pos: position(row, 'y_pos') + (depthAuthored ? dg : 0),
        z_pos: position(row, 'z_pos') + (depthAuthored ? 0 : dg) };
      corpse.release.push({ inventory_id: id, ...spot });
      const floorRow = clone(child);
      const inv = record(floorRow.inventory) ?? (floorRow.inventory = {} as Row) as Row;
      inv.possess_id = encounterId;
      inv.equipped = '';
      floorRow.position = spot;
      floorRow.computed = { ...(record(floorRow.computed) ?? {}), possessor: { type: 'encounter' } };
      released.push(floorRow);
    }
    return { corpse, released };
  };
  const corpseFrame = (frame: ObjectDestroyFrame, plan: { corpse?: ObjectDestroyCommand['corpse']; released: Row[] }): ObjectDestroyFrame => plan.corpse
    ? { ...frame, remove: [...frame.remove, ...plan.corpse.delete], released: [...(frame.released ?? []), ...plan.released],
      creaturesRemoved: [...(frame.creaturesRemoved ?? []), corpseId] }
    : frame;

  const templateId = upper(inventoryField(row, 'inventory_item_id'));
  if (!isUuid(templateId)) return unsupported('no-template');
  const template = read.definition(templateId);
  if (!template) return unsupported('template-not-in-catalog');
  const itemType = String(itemField(template, 'item_type') ?? itemField(row, 'item_type') ?? '');
  const templateName = String(itemField(template, 'item_name') ?? '');

  // Debris identity and the "Rubble, " name prefix both prevent re-rubbling.
  // R1b-2b: a payload-caused destruction of a pile is legacy's already-rubble branch (no new pile — it vanishes), not a refusal.
  const cause = input.cause && input.cause.kind === 'payload' && [input.cause.roll_id, input.cause.source_inventory_id, input.cause.ability_id].every(isUuid)
    ? { kind: 'payload' as const, roll_id: upper(input.cause.roll_id), source_inventory_id: upper(input.cause.source_inventory_id), ability_id: upper(input.cause.ability_id) }
    : null;
  if (input.cause && !cause) return unsupported('bad-cause');
  // destroyed BY DAMAGE (a payload's hit, or the GM spill's splash) rather than by the GM's own Turn to Rubble: a pile re-destroyed or a
  // composition that leaves nothing vanishes instead of being refused
  const byDamage = !!cause || input.splashChild === true;
  const alreadyRubble = /^Rubble,\s/.test(templateName) || isRubbleAnchorId(templateId);
  if (alreadyRubble && !byDamage) {
    return { supported: false, refused: true, message: 'This inventory is already rubble.' };
  }

  // R1 boundary: anything whose destruction touches OTHER rows or structure stays on the legacy lane — unless this
  // caller's commit persists that branch (R1b).
  const isDoor = itemType === 'door';
  if (isDoor && input.branches?.door !== true) return unsupported('door-link-migration');
  if (itemType === 'wall' && input.branches?.wall !== true) return unsupported('wall-run-breach');
  // A payload first: a container that also carries one (a brine barrel) still hands back as a payload — unless this caller can
  // drain it (R1b-2: `branches.payload` + the attributed caster; no caller passes it until Lane A runs payloads in the browser).
  const hasPayload = read.entries(row, 'destruction_effect_ability').length > 0;
  if (hasPayload && (input.branches?.payload !== true || !input.payloadCaster || !isUuid(input.payloadCaster.id))) return unsupported('destruction-payload');
  const container = isContainer(read, row, itemType);
  if (container && input.branches?.spill !== true) return unsupported('container-spill');

  // R1b-3 — the contents (spill_container_contents_and_apply_splash, which legacy runs FIRST — before the rubble / vanish branch). A child
  // that keeps its shape lands on the floor with its splash; a cohesion-losing one is deleted. Round 5 session C: inside a CAUSED
  // destruction a child the splash destroys is destroyed in turn (legacy apply_damage_to_target → handle_inventory_destroyed_from_damage(),
  // under the same roll group and caster): resolved here as its own destruction of the row as it lands on the floor at its spill slot,
  // broken by the splash (no damage type: legacy's minimal splash roll carries none). The GM tool's own spill keeps handing it back.
  const planContents = (): ContainerSpill | ObjectDestroyScope => {
    // autopilot W5 (session C follow-up 2): the GM tool's OWN spill too — a child its 1-point splash destroys (legacy runs the same
    // apply_damage_to_target → handle_inventory_destroyed_from_damage under the GM destruction's roll group and caster)
    const destroyChild = (child: Row, at: { x: number; y: number; z: number }, splashTotal: number): SpillChildDestruction => {
      const childId = upper(inventoryField(child, 'inventory_id'));
      const landed = clone(child) as Row;
      const inv = record(landed.inventory) ?? (landed.inventory = {} as Row) as Row;
      inv.possess_id = encounterId; // its own contents stay with it (a destroyed child container spills them in turn)
      landed.position = { x_pos: at.x, y_pos: at.y, z_pos: at.z };
      landed.computed = { ...(record(landed.computed) ?? {}), possessor: { type: 'encounter' } };
      const view: Row = { ...encounter, encounter_inventories_arr: [...roster, landed] };
      const geometry = read.drawnGeometry?.(landed) ?? null;
      // the destruction (and its payload origin) keeps the spill slot; its pile is grounded on the floor under it, as legacy's add_inventory
      // grounds every pile (Codex review: a slot > 0 would otherwise leave a floating pile at base z + slot × 0.35)
      const scope = resolveObjectDestruction({ ...input, inventoryId: childId, breakingDamage: splashTotal, destroyedByDamageTypes: [],
        sourceFootprintFt: geometry ? geometry.footprintM / METERS_PER_FOOT : null, sourceGeometry: geometry, ...(cause ? {} : { splashChild: true as const }) }, view, read);
      if (scope.supported) return scope.command;
      if (!scope.refused && (scope.anchorItemId || scope.definitionIds?.length)) return { definitionIds: scope.anchorItemId ? [scope.anchorItemId] : scope.definitionIds! };
      // N10: a splash-destroyed body whose creature this page has not loaded: the caller loads it (`corpse:<id>` fetch ids) and retries
      if (!scope.refused && scope.corpseCreatureIds?.length) return { definitionIds: scope.corpseCreatureIds.map(id => `corpse:${upper(id)}`) };
      if (!scope.refused && scope.linkFactIds?.length) return { reason: 'spill-child:waypoint-link-facts' };
      return { reason: `spill-child:${scope.refused ? 'refused' : scope.reason}` };
    };
    const plan = planSpill(read, row, { x: position(row, 'x_pos'), y: position(row, 'y_pos'), z: position(row, 'z_pos') }, input.breakingDamage ?? TURN_TO_RUBBLE_BREAKING_DAMAGE,
      destroyChild);
    if ('reason' in plan) return plan.definitionIds ? { supported: false, refused: false, reason: plan.reason, definitionIds: plan.definitionIds } : unsupported(plan.reason);
    return plan;
  };
  // The rows whose OWN link points at this row (re-aimed at whatever takes its place: a door's pile, a waypoint marker).
  const retargetTo = (to: string): LinkRetarget[] => roster.map(record).flatMap(other => !other || upper(inventoryField(other, 'inventory_id')) === inventoryId ? []
    : ownEncounterLinks(other).filter(link => upper(link.encounter_link_to) === inventoryId)
      .map(link => ({ inventory_id: upper(inventoryField(other, 'inventory_id')), encounter_link_id: upper(link.encounter_link_id), was: inventoryId, to })));
  /** A row's own links re-anchored on `to` (a self-link re-anchors BOTH ends, as the legacy helper's two UPDATEs do). */
  const migratedLinks = (links: EncounterLinkRow[], to: string): EncounterLinkRow[] => links.map(link => ({ ...clone(link), encounter_link_from: to,
    ...(upper(link.encounter_link_to) === inventoryId ? { encounter_link_to: to } : {}) }));

  // R1b-2b: a payload-caused VANISH — an already-rubble pile (rubble never re-rubbles; legacy skips both resolvers) or a composition
  // that leaves nothing. D-048 ports what used to hand back: a vanishing CONTAINER spills first (legacy runs the spill before either
  // branch); a DOOR that leaves no rubble, or an already-rubble pile carrying live links, leaves a WAYPOINT map marker its links migrate
  // onto (spawn_waypoint_map_marker_for_destroyed_door); any other vanishing row's links stay where they were (legacy leaves them).
  const vanish = (): ObjectDestroyScope => {
    const ownLinks = ownEncounterLinks(row);
    const incoming = retargetTo('');
    let linked = ownLinks.length > 0 || incoming.length > 0;
    if (!isDoor && alreadyRubble && !linked && read.hiddenLinks) {
      // a link from another encounter's row never rides this roster: the page asks for the fact before it decides (Codex review)
      const hidden = read.hiddenLinks(inventoryId);
      if (hidden === null) return { supported: false, refused: false, reason: 'waypoint-link-facts', linkFactIds: [inventoryId] };
      linked = hidden;
    }
    const wantsWaypoint = isDoor || (alreadyRubble && linked);
    const originalName = String(inventoryField(row, 'inventory_name') ?? templateName ?? '');
    const at = { x_pos: position(row, 'x_pos'), y_pos: position(row, 'y_pos'), z_pos: position(row, 'z_pos') };
    let waypoint: ObjectDestroyCommand['waypoint'];
    let markerRow: RubbleRosterRow | null = null;
    let retarget: LinkRetarget[] = [];
    if (wantsWaypoint) {
      const markerItemId = waypointMarkerFromHints(String(inventoryField(row, 'inventory_name') ?? ''), templateName);
      const marker = read.definition(markerItemId);
      if (!marker) return { supported: false, refused: false, reason: 'waypoint-marker-not-in-catalog', anchorItemId: markerItemId };
      const markerId = read.uuid();
      // add_inventory() names the row after its template; the door's position as it stood (legacy never grounds a waypoint)
      waypoint = { inventory_id: markerId, inventory_item_id: markerItemId, inventory_name: String(itemField(marker, 'item_name') ?? ''), ...at };
      retarget = incoming.map(r => ({ ...r, to: markerId }));
      markerRow = {
        inventory: { inventory_id: markerId, inventory_item_id: markerItemId, inventory_name: waypoint.inventory_name, possess_id: encounterId, item_qty: 1 },
        position: { ...at }, permissions: { player: true, campaign: true }, computed: { possessor: { type: 'encounter' } },
        ...(ownLinks.length > 0 ? { inventory_encounter_link: migratedLinks(ownLinks, markerId) } : {}),
      };
    }
    let spill: ContainerSpill | undefined;
    if (container) {
      const planned = planContents();
      if ('supported' in planned) return planned;
      spill = planned;
    }
    const body = planCorpse();
    if ('supported' in body) return body;
    const operationId = read.uuid();
    // R1b-2d: a vanishing object still enqueues its payload (legacy writes the vanish row, then ft_enqueue_environmental_payload).
    // (a GM splash child's job takes the root's roll group once the root stamps it — stampRollGroup)
    const payload = hasPayload ? snapshotDestructionPayload(read, row, { encounterId, sourceInventoryId: inventoryId, operationId: cause ? cause.roll_id : operationId,
      origin: at, caster: input.payloadCaster!, destroyedByDamageTypes: input.destroyedByDamageTypes ?? [] }) : null;
    if (hasPayload && !payload) return unsupported('destruction-payload-unreadable');
    return { supported: true, command: {
      schema: OBJECT_DESTROY_SCHEMA, operation_id: operationId, campaign_id: upper(input.campaignId), encounter_id: encounterId, inventory_id: inventoryId,
      before: { inventory_item_id: templateId, possess_id: encounterId, item_qty: Number(inventoryField(row, 'item_qty') ?? 1) || 1, inventory_deleted: 0, ...at },
      outcome: 'vanish', rubble: null,
      log: { key: 'inventory_destroyed_vanished', original_name: originalName },
      frame: corpseFrame(spillFrame(row, spill, encounterId, inventoryId, markerRow, retarget), body),
      ...(body.corpse ? { corpse: body.corpse } : {}),
      ...(spill ? { spill } : {}),
      ...(payload ? { payload } : {}),
      ...(waypoint ? { waypoint } : {}),
      ...(cause ? { cause } : {}),
    } };
  };
  if (alreadyRubble) return vanish();
  const compositionId = layerUuid(read, row, 'item_composition');
  // D-048: damage destroys an object without a composition; the GM tool requires a known composition.
  if (!compositionId && byDamage) return vanish();
  const spawn = rubbleSpawnEntry(compositionId);
  if (!spawn) return unsupported('unknown-composition');
  // The GM tool refuses a composition that leaves no rubble; damage vanishes it.
  if (spawn.fate === 'vanishes') return byDamage ? vanish() : { supported: false, refused: true, message: LEGACY_NOT_ELIGIBLE };
  const anchor = spawn.anchor;
  // A missing anchor is an explicit unsupported catalog gap.
  if (!anchor) return unsupported('no-collapsed-rubble-anchor');
  // A page catalog holds only what the encounter held at boot, so the FIRST pile of a composition is usually absent.
  if (!read.definition(anchor.itemId)) return { supported: false, refused: false, reason: 'rubble-anchor-not-in-catalog', anchorItemId: anchor.itemId };

  // R1b-3 — the contents. Only a spill WITHOUT splash converts: every row the break reaches loses cohesion and is deleted.
  // A child that keeps its shape is reparented and takes splash damage — object damage, R2's resolver — so it hands back.
  // R1b-4 — a room-generated WALL RUN (DAFT3D S14/S21, the legacy breach branch). Only a row bound to a run converts: a
  // painted-wall section or a hand-placed wall has no `wallRun` binding and keeps handing back.
  let wall: { key: string; kind: 'section' | 'run'; run: WallRunBinding; record: Row } | undefined;
  if (itemType === 'wall') {
    const generation = generationOf(row);
    const run = wallRunBindingOf(generation?.wallRun);
    if (!generation || !run) {
      // N10 (R-021 step 2): a wall bound to no run — hand-placed, or a generated row without a run binding — is an ordinary object to
      // legacy (ft_inventory_bound_wall_run → null: the plain rubble path) unless a painted-wall anchor binds it (ft_painted_wall_destroy_section
      // runs first and vanishes it). The only authored painted section template is Ice, which leaves no rubble, so the GM tool refused it
      // above in both lanes; a painted row reaching here (a future rubble-leaving profile) or one the page cannot judge still hands back.
      const painted = read.paintedWallBound?.(inventoryId) ?? null;
      if (painted !== false) return unsupported(painted ? 'painted-wall-section' : 'wall-unbound');
    } else {
      const named = (v: unknown): boolean => typeof v === 'string' && v.trim() !== '';
      // A section carries its native breach record on the pile; otherwise the whole run opens in place.
      const section = run.span.max - run.span.min <= WALL_RUN_SECTION_FT + 1e-6 && named(generation.roomId) && named(generation.placementId);
      wall = { key: run.key, kind: section ? 'section' : 'run', run, record: generation };
    }
  }
  let spill: ContainerSpill | undefined;
  if (container) {
    const planned = planContents();
    if ('supported' in planned) return planned;
    spill = planned;
  }

  const originalName = String(inventoryField(row, 'inventory_name') ?? templateName ?? '');
  const body = planCorpse();
  if ('supported' in body) return body;
  const before = {
    inventory_item_id: templateId,
    possess_id: encounterId,
    item_qty: Number(inventoryField(row, 'item_qty') ?? 1) || 1,
    inventory_deleted: 0 as const,
    x_pos: position(row, 'x_pos'),
    y_pos: position(row, 'y_pos'),
    z_pos: position(row, 'z_pos'),
  };
  const operationId = read.uuid();
  // R1b-2: the snapshot, taken from the row as it stands (the frame below has not touched it). Legacy anchors on the object's tile.
  // R1b-2d: a CAUSED destruction's job runs under the parent's roll group (legacy enqueues it with the same `$roll_id`).
  const payload = hasPayload ? snapshotDestructionPayload(read, row, { encounterId, sourceInventoryId: inventoryId, operationId: cause ? cause.roll_id : operationId,
    origin: { x_pos: before.x_pos, y_pos: before.y_pos, z_pos: before.z_pos }, caster: input.payloadCaster!,
    destroyedByDamageTypes: input.destroyedByDamageTypes ?? [] }) : null;
  if (hasPayload && !payload) return unsupported('destruction-payload-unreadable');
  if (wall?.kind === 'run') {
    return { supported: true, command: {
      schema: OBJECT_DESTROY_SCHEMA, operation_id: operationId, campaign_id: upper(input.campaignId), encounter_id: encounterId, inventory_id: inventoryId,
      before, outcome: 'breach', rubble: null,
      // "this run is rubble" IS the opening it left: the rubble log line, with no pile.
      log: { key: 'inventory_destroyed_rubble', original_name: originalName },
      frame: { remove: [], add: [], vitality: [{ inventory_id: inventoryId, v_current: 0 }] },
      wall: { key: wall.key, kind: 'run' },
      ...(payload ? { payload } : {}),
    } };
  }

  // RubbleTemplate::resolve(): NULL inventory_size = the anchor's own default size.
  const sizeId = sourceSizeId(read, row, template);
  const inventorySize = anchor.defaultSizeId && anchor.defaultSizeId === sizeId ? null : sizeId;
  // A carved wall row describes the section that broke, not a mesh: ft_rubble_fit_transform() sizes its pile from the run's
  // span and records height/span without a volume (`wall-section` basis) — the same here.
  const wallSpanFt = wall ? wall.run.span.max - wall.run.span.min : null;
  const transform = fitRubbleTransform(read, anchor.itemId, sizeId, wallSpanFt ?? input.sourceFootprintFt);
  const sourceGeometry = wall
    ? normalizeRubbleSource({ schema: 'ft.rubble-source.v1', basis: 'wall-section',
      heightM: (wall.run.z.max - wall.run.z.min) * METERS_PER_FOOT, footprintM: wallSpanFt! * METERS_PER_FOOT })
    : normalizeRubbleSource(input.sourceGeometry);
  if (transform && sourceGeometry) transform.rubbleSource = sourceGeometry;
  const rubbleId = read.uuid();
  const rubbleName = `${originalName} Rubble`;
  const x = before.x_pos;
  const y = before.y_pos;
  // A wall SECTION's rubble stands exactly in the gap, on the section's floor (legacy re-seats it at the run's z.min).
  const z = wall ? wall.run.z.min : read.floorAt(x, y);
  const breachRecord: Row | null = wall ? (() => {
    const { wallRun: _run, pinned: _pinned, fingerprint: _fp, ...rest } = wall.record;
    return { ...rest, placementId: `wall-breach-${wall.key}`, requestId: `wall-breach:${wall.key}`,
      carvedFrom: String(wall.record.carvedFrom ?? wall.record.placementId), wallBreach: wall.run };
  })() : null;

  // R1b-1 — the door's links re-anchor on its rubble (handle_inventory_destroyed_from_damage: "door -> rubble (links
  // migrate to rubble)"): its own links move their `from`, and every roster row whose link points AT the door is re-aimed.
  // A door reaching this point always leaves rubble (a vanishing or anchorless door was refused / handed back above),
  // so the legacy waypoint fallback is unreachable from the GM tool.
  const doorLinks = isDoor ? ownEncounterLinks(row) : [];
  const retarget: LinkRetarget[] = isDoor ? retargetTo(rubbleId) : [];

  const addRow: RubbleRosterRow = {
    inventory: {
      inventory_id: rubbleId,
      inventory_item_id: anchor.itemId,
      inventory_name: rubbleName,
      possess_id: encounterId,
      item_qty: 1,
      ...(inventorySize ? { inventory_size: inventorySize } : {}),
      ...(transform ? { transform_3d: clone(transform) } : {}),
      ...(breachRecord ? { inventory_generation: clone(breachRecord) } : {}),
    },
    position: { x_pos: x, y_pos: y, z_pos: z },
    permissions: { player: true, campaign: true },
    computed: { possessor: { type: 'encounter' } },
    ...(doorLinks.length > 0 ? { inventory_encounter_link: migratedLinks(doorLinks, rubbleId) } : {}),
  };

  // the GM tool's own destruction: every child its spill's splash destroyed logs under THIS roll group (their payload jobs too)
  if (!cause && input.splashChild !== true) for (const nested of spill?.destroyed ?? []) stampRollGroup(nested, operationId);
  return {
    supported: true,
    command: {
      schema: OBJECT_DESTROY_SCHEMA,
      operation_id: operationId,
      campaign_id: upper(input.campaignId),
      encounter_id: encounterId,
      inventory_id: inventoryId,
      before,
      outcome: 'rubble',
      rubble: {
        inventory_id: rubbleId,
        inventory_item_id: anchor.itemId,
        inventory_size: inventorySize,
        inventory_name: rubbleName,
        x_pos: x,
        y_pos: y,
        z_pos: z,
        transform_3d: transform,
      },
      log: { key: 'inventory_destroyed_rubble', original_name: originalName },
      frame: corpseFrame(spillFrame(row, spill, encounterId, inventoryId, addRow, retarget), body),
      ...(body.corpse ? { corpse: body.corpse } : {}),
      ...(spill ? { spill } : {}),
      ...(wall ? { wall: { key: wall.key, kind: wall.kind } } : {}),
      ...(payload ? { payload } : {}),
      ...(cause ? { cause } : {}),
      ...(isDoor ? { links: {
        from: doorLinks.map(link => upper(link.encounter_link_id)).sort(),
        // {link, anchor}: the anchor is part of the identity — a link re-homed to another row is stale (Codex R1b-1).
        to: retarget.map(r => ({ encounter_link_id: r.encounter_link_id, inventory_id: r.inventory_id }))
          .sort((a, b) => `${a.encounter_link_id}@${a.inventory_id}`.localeCompare(`${b.encounter_link_id}@${b.inventory_id}`)),
      } } : {}),
    },
  };
}

/** Re-aim each retargeted roster link from `expect(r)` to `pick(r)` (door → rubble on apply, rubble → door on revert). A link
 * that no longer holds the expected end changed under this operation (a newer frame, a saved row): it is left alone. */
function retargetLinks(roster: Row[], retarget: LinkRetarget[] | undefined, expect: (r: LinkRetarget) => string, pick: (r: LinkRetarget) => string): boolean {
  let changed = false;
  for (const r of retarget ?? []) {
    const row = roster.find(candidate => upper(inventoryField(candidate, 'inventory_id')) === upper(r.inventory_id));
    for (const link of row ? ownEncounterLinks(row) : []) {
      if (upper(link.encounter_link_id) === upper(r.encounter_link_id) && upper(link.encounter_link_to) === upper(expect(r))) {
        link.encounter_link_to = pick(r);
        changed = true;
      }
    }
  }
  return changed;
}

/**
 * Apply a destruction frame to an encounter roster IN PLACE (initiator and observers alike). Idempotent: a row
 * already removed stays removed, a rubble row already present is not duplicated. Returns true when it changed.
 * ⚠ Ordering/ownership (a delayed apply after a revert) is the OUTBOX's job — it dedupes by operation_id (Codex F3).
 */
export function applyObjectDestruction(encounter: Row, frame: ObjectDestroyFrame): boolean {
  let changed = false;
  const members = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [];
  const creaturesRemoved = new Set((frame.creaturesRemoved ?? []).map(upper));
  for (let i = members.length - 1; i >= 0; i--) {
    if (creaturesRemoved.has(upper(bucketField(members[i] ?? null, 'encounter_creatures', 'encounter_creature_id')))) { members.splice(i, 1); changed = true; }
  }
  const roster = Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : null;
  if (!roster) return changed;
  const remove = new Set(frame.remove.map(upper));
  for (let i = roster.length - 1; i >= 0; i--) {
    if (remove.has(upper(inventoryField(roster[i] ?? null, 'inventory_id')))) { roster.splice(i, 1); changed = true; }
  }
  for (const add of frame.add) {
    const id = upper(add.inventory.inventory_id);
    if (!roster.some(r => upper(inventoryField(r, 'inventory_id')) === id)) {
      // A plain-data row (no Dates/Maps): a JSON clone keeps the frame immutable for a later revert.
      roster.push(clone(add) as unknown as Row);
      changed = true;
    }
  }
  if (retargetLinks(roster, frame.retarget, r => r.was, r => r.to)) changed = true;
  for (const released of frame.released ?? []) {
    const id = upper(inventoryField(released, 'inventory_id'));
    if (!roster.some(r => upper(inventoryField(r, 'inventory_id')) === id)) { roster.push(clone(released)); changed = true; }
  }
  for (const v of frame.vitality ?? []) {
    const row = roster.find(r => upper(inventoryField(r, 'inventory_id')) === upper(v.inventory_id));
    if (row && setRowVitality(row, v.v_current)) changed = true;
  }
  return changed;
}

/** Undo a frame (correction / terminal failure): drop the rubble rows, restore the source rows given. */
export function revertObjectDestruction(encounter: Row, frame: ObjectDestroyFrame, restoreRows: Row[], restoreCreatures: Row[] = []): void {
  if (restoreCreatures.length && !Array.isArray(encounter.encounter_creatures_arr)) encounter.encounter_creatures_arr = [];
  const members = (encounter.encounter_creatures_arr ?? []) as Row[];
  for (const member of restoreCreatures) {
    const id = upper(bucketField(member, 'encounter_creatures', 'encounter_creature_id'));
    if (!members.some(r => upper(bucketField(r, 'encounter_creatures', 'encounter_creature_id')) === id)) members.push(clone(member));
  }
  const roster = Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : null;
  if (!roster) return;
  const added = new Set([...frame.add.map(a => upper(a.inventory.inventory_id)), ...(frame.released ?? []).map(r => upper(inventoryField(r, 'inventory_id')))]);
  for (let i = roster.length - 1; i >= 0; i--) {
    if (added.has(upper(inventoryField(roster[i] ?? null, 'inventory_id')))) roster.splice(i, 1);
  }
  for (const row of restoreRows) {
    const id = upper(inventoryField(row, 'inventory_id'));
    // Clone: the caller's snapshot must stay reusable (a later roster mutation must not reach it — Codex F3).
    if (!roster.some(r => upper(inventoryField(r, 'inventory_id')) === id)) roster.push(clone(row));
  }
  retargetLinks(roster, frame.retarget, r => r.to, r => r.was);
  // A breached run stayed in the roster: put the snapshot back in its place.
  for (const v of frame.vitality ?? []) {
    const index = roster.findIndex(r => upper(inventoryField(r, 'inventory_id')) === upper(v.inventory_id));
    const snapshot = restoreRows.find(r => upper(inventoryField(r, 'inventory_id')) === upper(v.inventory_id));
    if (index >= 0 && snapshot) roster[index] = clone(snapshot);
  }
}

/** A GM-tool spill child (no payload cause) and everything its own spill destroyed: rows and payload jobs under the root's roll group. */
function stampRollGroup(command: ObjectDestroyCommand, rollGroup: string): void {
  if (command.cause) return;
  command.roll_group = rollGroup;
  if (command.payload) command.payload = { ...command.payload, roll_id: rollGroup };
  for (const nested of command.spill?.destroyed ?? []) stampRollGroup(nested, rollGroup);
}

/** A destruction's roster frame: a child the splash destroyed never reached any roster, so its pile (and anything ITS destruction leaves)
 * lands before this one's, as legacy writes them; then the released children, the row's own removal and what takes its place (a pile, a
 * waypoint marker, nothing). */
function spillFrame(row: Row, spill: ContainerSpill | undefined, encounterId: string, inventoryId: string, placed: RubbleRosterRow | null,
  retarget: LinkRetarget[]): ObjectDestroyFrame {
  const nested = spill?.destroyed ?? [];
  const released = [...nested.flatMap(c => c.frame.released ?? []), ...(spill?.release ?? []).map(r => releasedRow(row, r, encounterId))];
  const nestedRetarget = nested.flatMap(c => c.frame.retarget ?? []);
  // N10: a splash-destroyed BODY's creature leaves the roster with it (its corpse frame names it)
  const creaturesRemoved = nested.flatMap(c => c.frame.creaturesRemoved ?? []);
  return { remove: [...nested.flatMap(c => c.frame.remove), inventoryId], add: [...nested.flatMap(c => c.frame.add), ...(placed ? [placed] : [])],
    ...(retarget.length + nestedRetarget.length > 0 ? { retarget: [...nestedRetarget, ...retarget] } : {}),
    ...(released.length > 0 ? { released } : {}), ...(creaturesRemoved.length > 0 ? { creaturesRemoved } : {}) };
}

/** A released child as a floor row: its nested row, reparented to the encounter at the spill spot, at its splashed vitality. */
function releasedRow(container: Row, release: SpillRelease, encounterId: string): Row {
  const find = (rows: Row[]): Row | null => {
    for (const r of rows) {
      if (upper(inventoryField(r, 'inventory_id')) === release.inventory_id) return r;
      const inner = find(contentsOf(r));
      if (inner) return inner;
    }
    return null;
  };
  const row = clone(find(contentsOf(container)) ?? { inventory: { inventory_id: release.inventory_id } }) as Row;
  const inv = record(row.inventory) ?? (row.inventory = {} as Row) as Row;
  inv.possess_id = encounterId;
  row.position = { x_pos: release.x_pos, y_pos: release.y_pos, z_pos: release.z_pos };
  setRowVitality(row, release.v_current);
  ((row.computed as Row).vitality as Row).v_temp = release.v_temp;
  (row.computed as Row).possessor = { type: 'encounter' };
  return row;
}

/** A row's current vitality as every reader takes it — the instance column and the row's own compiled cluster (the scene's
 * breach overlay reads `computed.vitality`; a lean row that omitted it gains one). */
function setRowVitality(row: Row, current: number): boolean {
  const inv = record(row.inventory) ?? row;
  if (!record(row.computed)) row.computed = {};
  const computed = row.computed as Row;
  if (!record(computed.vitality)) computed.vitality = {};
  const vitality = computed.vitality as Row;
  const before = JSON.stringify([inv.v_current, vitality.v_current, vitality.base_v_current]);
  inv.v_current = current;
  vitality.v_current = current;
  vitality.base_v_current = current;
  return JSON.stringify([inv.v_current, vitality.v_current, vitality.base_v_current]) !== before;
}
